// src/components/Seo.js
// Métadonnées de référencement, communes à toutes les pages :
//   - titre et description ;
//   - URL canonique et versions linguistiques (hreflang fr / ar / en + x-default) ;
//   - partage sur les réseaux sociaux (Open Graph et Twitter) ;
//   - données structurées JSON-LD pour les moteurs de recherche.
import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { SITE_URL } from "../config";
import { normalizeLang, tf } from "../utils/formation";
import ogFr from "../assets/og-ccma-fr.jpg";
import ogAr from "../assets/og-ccma-ar.jpg";

const LANGS = ["fr", "ar", "en"];
const OG_LOCALES = { fr: "fr_DZ", ar: "ar_DZ", en: "en_US" };
const OG_IMAGES = { fr: ogFr, ar: ogAr, en: ogFr };

/** URL absolue d'une page : absoluteUrl("formation", "ar") -> https://ccma.caci.dz/ar/formation */
export function absoluteUrl(path = "", lang = "fr") {
  const clean = String(path || "").replace(/^\/+|\/+$/g, "");
  const prefix = lang === "fr" ? "" : `/${lang}`;
  return `${SITE_URL}${prefix}${clean ? `/${clean}` : ""}`;
}

export default function Seo({
  path = "",
  title,
  description,
  image,
  type = "website",
  noindex = false,
  breadcrumb,
  jsonLd,
  publishedTime,
}) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const siteName = t("brand.full");
  const fullTitle = title ? `${title} — ${t("brand.short")}` : `${t("brand.short")} — ${siteName}`;
  const desc = description || tf(t, lang, "seo.default_description");
  const hasPath = typeof path === "string";
  const canonical = hasPath ? absoluteUrl(path, lang) : null;
  const socialImage = image && /^https?:\/\//i.test(image) ? image : `${SITE_URL}${image || OG_IMAGES[lang]}`;

  const blocks = [];
  if (breadcrumb && breadcrumb.length > 0) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumb.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: absoluteUrl(item.path, lang),
      })),
    });
  }
  if (jsonLd) blocks.push(...(Array.isArray(jsonLd) ? jsonLd : [jsonLd]));

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      {canonical ? LANGS.map((code) => (
        <link key={code} rel="alternate" hrefLang={code} href={absoluteUrl(path, code)} />
      )) : null}
      {canonical ? <link rel="alternate" hrefLang="x-default" href={absoluteUrl(path, "fr")} /> : null}
      {noindex ? <meta name="robots" content="noindex, follow" /> : null}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title || siteName} />
      <meta property="og:description" content={desc} />
      {canonical ? <meta property="og:url" content={canonical} /> : null}
      <meta property="og:image" content={socialImage} />
      <meta property="og:image:alt" content={tf(t, lang, "seo.image_alt")} />
      <meta property="og:locale" content={OG_LOCALES[lang]} />
      {LANGS.filter((code) => code !== lang).map((code) => (
        <meta key={code} property="og:locale:alternate" content={OG_LOCALES[code]} />
      ))}
      {publishedTime ? <meta property="article:published_time" content={publishedTime} /> : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || siteName} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={socialImage} />

      {blocks.map((block, index) => (
        <script key={index} type="application/ld+json">{JSON.stringify(block)}</script>
      ))}
    </Helmet>
  );
}
